import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TaskAttentionIcon, TagIcon, MotivationIcon } from '@/src/renderer/components/icons'
import { RewardCatalogIcon } from '@/src/renderer/components/ui/RewardCatalogIcon'
import { getRewardById, rewardCta, type CatalogLanguage } from '@/src/renderer/lib/data/rewardCatalog'
import { AttentionDropdownContent } from '../../AttentionDropdown'
import { TagDropdownContent } from '../../TagDropdown'
import { RewardPickerContent } from '../../RewardPicker'
import { RewardDropdownContent } from '../../RewardDropdown'
import { useTags } from '../../../hooks/useTags'
import type { DetailsValues } from '../detailsValues'
import type { DetailsChipId } from '../useDetailsSections'
import type { TaskDetailsUpdate } from '../TaskDetails.types'
import { DetailsChip } from './DetailsChip'
import { DetailsReminderChip } from './DetailsReminderChip'

/** Das Wort zur Stufe — „Als erstes", „Dran denken", „Wenn möglich". */
const ATTENTION_LABEL = {
  high: 'taskCreation.attentionHigh',
  medium: 'taskCreation.attentionMedium',
  low: 'taskCreation.attentionLow',
} as const

export interface DetailsChipRowProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Die erste Aufgabe der Auswahl — über sie wird geschrieben. */
  anchorId: string
  /** Was ALLE Ausgewählten tragen. */
  values: DetailsValues
  onUpdate: TaskDetailsUpdate
  openChip: DetailsChipId | null
  onOpenChipChange: (id: DetailsChipId | null) => void
  /** Ohne Belohnungs-Chip — eine **Unteraufgabe** trägt keine (T14 #179). */
  withoutReward?: boolean
  /** Die Aufgabe, deren Erinnerungen der vierte Chip zeigt — nur bei EINER; sonst kein Chip. */
  reminderTaskId?: string
  /** Der Chip „Erinnerung“ geht beim Anlegen auf „Datum & Uhrzeit“ auf (der Menüeintrag). */
  reminderTab?: 'at' | null
}

/**
 * **Die Chips im Kopf der Aufgaben-Details** — Aufmerksamkeit, Tags,
 * **Belohnung** (entschieden 2026-09-19), seit dem 30.09.2026 auch die **Erinnerung**
 * (Plan task-reminders, Entscheidung 12). Sie ersetzen die drei Bereiche, die bis
 * dahin offen im Rumpf standen und dort die halbe Rolllänge kosteten, obwohl man
 * sie selten mehr als einmal anfasst.
 *
 * **Jeder Chip zeigt den WERT, sobald einer da ist**, sonst seine Beschriftung.
 * Das **Aufgaben-Menü** macht es umgekehrt (ein **Haken**, nie der Wert) — dort
 * stehen die Werte nebenan an der Zeile. Die Aufgaben-Details sind der Ort, der
 * eine Aufgabe GANZ zeigt; ein Chip mit bloßer Beschriftung machte ausgerechnet
 * dort drei Werte unsichtbar. Der Preis ist eine Zeile, die umbrechen darf.
 *
 * Das Wort ist **Aufmerksamkeit**, nicht „Priorität" (CONTEXT.md, Flagged
 * ambiguities): Die drei Stufen sind eine Haltung, keine Rangfolge.
 *
 * @example
 * <DetailsChipRow anchorId={anchor.id} values={values} onUpdate={onUpdate}
 *   openChip={openChip} onOpenChipChange={setOpenChip} />
 */
export const DetailsChipRow: React.FC<DetailsChipRowProps> = ({
  anchorId, values, onUpdate, openChip, onOpenChipChange, withoutReward = false, reminderTaskId, reminderTab, ...rest
}) => {
  const { t, i18n } = useTranslation()
  const language: CatalogLanguage = i18n.language?.startsWith('en') ? 'en' : 'de'
  const { tags: allTags } = useTags()

  const toggle = useCallback(
    (id: DetailsChipId) => (open: boolean) => onOpenChipChange(open ? id : null),
    [onOpenChipChange],
  )

  // ── Aufmerksamkeit ──
  const attention = values.attention

  // ── Tags ──
  const chosen = allTags.filter(tag => values.tags.includes(tag.id))

  // ── Belohnung ──
  const reward = getRewardById(values.reward)
  /*
    Welchen der beiden Schirme das eine Popover zeigt — dieselbe Mechanik wie in
    `TaskRewardTrigger`: Eine angehängte Belohnung überspringt das Vorschaltmenü.
    Die Ref spiegelt den Zustand, damit `handleMenuClose` ihn gleich lesen kann.
  */
  const [rewardView, setRewardView] = useState<'menu' | 'picker'>(reward ? 'picker' : 'menu')
  const rewardViewRef = useRef(rewardView)
  rewardViewRef.current = rewardView

  useEffect(() => {
    if (openChip === 'reward') return
    const next = reward ? 'picker' : 'menu'
    setRewardView(next)
    rewardViewRef.current = next
  }, [openChip, reward])

  const handleRewardSelect = useCallback((action: 'reward' | 'goal') => {
    if (action === 'reward') {
      // Den Schirm tauschen statt zu schließen — ein zweites Popover bräuchte
      // seinen eigenen Anker und käme mit sichtbarem Flackern zurück.
      setRewardView('picker')
      rewardViewRef.current = 'picker'
      return
    }
    // „Mein Ziel dahinter" hat noch keinen Weg dahinter (CONTEXT.md, Flagged
    // ambiguities: „Mein Ziel dahinter" vs Ziel) — schließen ist alles.
    onOpenChipChange(null)
  }, [onOpenChipChange])

  // Das Menü ruft `onClose` gleich nach `onSelect`; genau diesen einen Aufruf
  // verschlucken, sonst ginge das Popover unter dem Wechsel zu.
  const handleRewardMenuClose = useCallback(() => {
    if (rewardViewRef.current === 'picker') return
    onOpenChipChange(null)
  }, [onOpenChipChange])

  return (
    <div className="details-chip-row" {...rest}>
      <DetailsChip
        id="attention"
        icon={<TaskAttentionIcon size={16} level={attention ?? undefined} />}
        filled={attention != null}
        open={openChip === 'attention'}
        onOpenChange={toggle('attention')}
        width={220}
        content={
          <AttentionDropdownContent
            value={attention}
            placement="badge"
            onValueChange={value => { onUpdate(anchorId, { attention: value }); onOpenChipChange(null) }}
            onClose={() => onOpenChipChange(null)}
            // **Nur, wenn etwas dasteht** (Nutzer-Vorgabe, 2026-09-19; ADR 0026):
            // Ein „Entfernen" über einem leeren Wert verspricht eine Handlung,
            // die es nicht gibt. Ohne den Griff lässt der Inhalt den ganzen Fuß
            // weg — er rendert ihn nur, wenn er ihn bekommt.
            onRemove={attention == null ? undefined
              : () => { onUpdate(anchorId, { attention: null }); onOpenChipChange(null) }}
          />
        }
      >
        {attention ? t(ATTENTION_LABEL[attention]) : t('taskDetails.section.attention')}
      </DetailsChip>

      <DetailsChip
        id="tags"
        icon={<TagIcon size={16} />}
        filled={chosen.length > 0}
        open={openChip === 'tags'}
        onOpenChange={toggle('tags')}
        content={
          <TagDropdownContent
            value={values.tags}
            placement="badge"
            onValueChange={ids => onUpdate(anchorId, { tags: ids })}
            onClose={() => onOpenChipChange(null)}
            // Dieselbe Regel. Der Fuß bleibt hier trotzdem stehen — die
            // Tag-Auswahl ist bis zum **Bestätigen** örtlich, und ohne den Knopf
            // käme sie nie an.
            onRemoveAll={chosen.length === 0 ? undefined
              : () => { onUpdate(anchorId, { tags: [] }); onOpenChipChange(null) }}
          />
        }
      >
        {chosen.length > 0
          /* Die Namen, nicht ihre Zahl: „3 Tags" wäre wieder ein Nachschlagen. */
          ? chosen.map(tag => (
            <span key={tag.id} className="details-chip__tag" data-color={tag.color}>{tag.name}</span>
          ))
          : t('taskDetails.section.tags')}
      </DetailsChip>

      {!withoutReward && <DetailsChip
        id="reward"
        icon={reward
          ? <RewardCatalogIcon rewardId={reward.id} name="" size={20} />
          : <MotivationIcon size={16} />}
        filled={reward != null}
        open={openChip === 'reward'}
        onOpenChange={toggle('reward')}
        content={rewardView === 'picker' ? (
          <RewardPickerContent
            value={values.reward}
            placement="badge"
            onValueChange={id => { onUpdate(anchorId, { rewardId: id }); onOpenChipChange(null) }}
            onClose={() => onOpenChipChange(null)}
          />
        ) : (
          <RewardDropdownContent
            placement="badge"
            onSelect={handleRewardSelect}
            onClose={handleRewardMenuClose}
          />
        )}
      >
        {/* Dieselbe Sprache wie am Griff der aufgeklappten Karte: die Belohnung
            als Handlung („Süße Nascherei zwischendurch"), leer als „Belohnung / Ziel". */}
        {reward ? rewardCta(reward, language) : t('taskCreation.rewardTrigger.placeholder')}
      </DetailsChip>}

      {reminderTaskId && (
        <DetailsReminderChip taskId={reminderTaskId} open={openChip === 'reminders'} onOpenChange={toggle('reminders')} tab={reminderTab ?? undefined} />
      )}
    </div>
  )
}
